import { pipToDecimal } from '@katanaperps/katana-perps-sdk';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';

import useEventCallback from '#/hooks/useEventCallback';
import { transformConfigurationFields } from '#/lib/ethereum/contracts/vaults/vaultsConfigContracts';
import {
  getExchangeContract,
  getVaultAggregatorContract,
  getVaultProviderContract,
  LoadVaultDepositorsResultStructOutput,
} from '#/lib/ethereum/contracts/vaults/vaultsContract';

import { minutes, seconds } from './utils';

export function useInvalidateAllOnChainVaultsQueries() {
  const queryClient = useQueryClient();
  const invalidate = useEventCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ['vaultsOnChain'],
    });
  });
  return invalidate;
}

export function useVaultOnChainConfigurationsQuery({
  managerWallet,
}: {
  managerWallet: string;
}) {
  return useQuery({
    queryKey: ['vaultsOnChain', 'configurations', managerWallet],
    queryFn: () =>
      getVaultAggregatorContract()
        .loadVaultConfigurations(managerWallet)
        .catch(error => {
          console.warn(error);
          throw error;
        })
        .then(response => response.map(transformConfigurationFields))
        .then(response => response.reverse()),
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainPendingConfigurationQuery({
  managerWallet,
}: {
  managerWallet: string;
}) {
  return useQuery({
    queryKey: ['vaultsOnChain', 'pendingConfigurations', managerWallet],
    queryFn: () =>
      getVaultProviderContract()
        .vaultConfigurationUpgradesByManagerWallet(managerWallet)
        .then(response => ({
          blockTimestampThreshold: response.blockTimestampThreshold,
          exists: response.exists,
          vaultConfigurationFieldsTransformed: response.exists
            ? transformConfigurationFields(response.newVaultConfigurationFields)
            : undefined,
        })),
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainBalanceForWalletQuery({
  enabled,
  managerWallet,
  depositorWallet,
}: {
  enabled?: boolean;
  managerWallet: string;
  depositorWallet: string;
}) {
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'loadVaultBalanceForWalletSummary',
      managerWallet,
      depositorWallet,
    ],
    queryFn: !enabled
      ? skipToken
      : async () => {
          const balance =
            await getVaultProviderContract().loadVaultBalanceForWalletSummary(
              managerWallet,
              depositorWallet
            );
          if (typeof balance?.owedQuantity === 'undefined') {
            // Basic runtime structure check
            throw new Error(
              'Incorrect loadVaultBalanceForWalletSummary response structure'
            );
          }
          return {
            costBasis: balance.costBasis,
            initialDepositTimestampInS: balance.initialDepositTimestampInS,
            lockedQuantity: balance.lockedQuantity,
            owedQuantity: balance.owedQuantity,
            pendingDepositQuantity: balance.pendingDepositQuantity,
            quantityAvailableToWithdraw: balance.quantityAvailableToWithdraw,
            withdrawalLimitWindowEndTimestampInS:
              balance.withdrawalLimitWindowEndTimestampInS,
          };
        },
    staleTime: seconds(7),
    refetchInterval: minutes(4),
  });
}

// export function useVaultOnChainConfigurationQuery({
//   managerWallet,
// }: {
//   managerWallet: string;
// }) {
//   return useQuery({
//     queryKey: ['vaultsOnChain', 'configuration', managerWallet],
//     queryFn: () => getVaultContract().loadVaultConfiguration(managerWallet, 2),
//     staleTime: seconds(15),
//     refetchInterval: minutes(5),
//   });
// }

// const fakeWithdrawalQueue = [
//   {
//     nonce: 24889031647993638872876733253896480994n,
//     depositorWallet: '0x742d35Cc6631C0532925a3b8D581C027d2b1c80C',
//     grossQuantity: 99996895700n,
//   },
//   {
//     nonce: 25411145238962640857750054050518678464n,
//     depositorWallet: '0x8ba1f109551bD432803012645Hac136c22C501e',
//     grossQuantity: 89996895700n,
//   },
//   {
//     nonce: 25975249756064202941970855221582487263n,
//     depositorWallet: '0x4B20993Bc481177ec7E8f571ceCaE8A9e22C02db',
//     grossQuantity: 89996895700n,
//   },
// ];

export function useVaultOnChainWithdrawalQueueQuery({
  managerWallet,
  reduceInternalForProcessing,
}: {
  managerWallet: string | undefined;
  reduceInternalForProcessing?: boolean;
}) {
  return useQuery({
    queryKey: ['vaultsOnChain', 'withdrawalQueue', managerWallet],
    queryFn: !managerWallet
      ? skipToken
      : async () => {
          const contractResponse =
            await getVaultAggregatorContract().loadVaultWithdrawalQueue(
              managerWallet
            );
          return contractResponse.map(item => ({
            depositorWallet: item.depositorWallet,
            grossQuantity: item.grossQuantity,
            nonce: item.nonce,
          }));
        },
    staleTime: seconds(15),
    refetchInterval: data =>
      data.state.data &&
      data.state.data?.length > 0 &&
      reduceInternalForProcessing
        ? seconds(11)
        : minutes(1),
  });
}

export function useVaultOnChainDepositorsMapQuery({
  managerWallet,
  depositors,
}: {
  managerWallet: string;
  depositors: string[] | undefined;
}) {
  return useQuery({
    queryKey: ['vaultsOnChain', 'depositors', managerWallet, depositors],
    queryFn: !depositors
      ? skipToken
      : async () => {
          const responseList =
            await getVaultAggregatorContract().loadVaultDepositorsByManagerWallet(
              managerWallet,
              depositors
            );
          return depositors.reduce(
            (map, depositor, index) => {
              const depositorObj = responseList[index];
              map[depositor] = {
                costBasis: depositorObj.costBasis,
                initialDepositTimestampInS:
                  depositorObj.initialDepositTimestampInS,
                owedQuantity: depositorObj.owedQuantity,
              };
              return map;
            },
            {} as {
              [depositor: string]: LoadVaultDepositorsResultStructOutput;
            }
          );
        },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

type OnChainVaultDepositorsMap = {
  [managerWallet: string]: LoadVaultDepositorsResultStructOutput | undefined;
};

export function useVaultOnChainMulticallDepositorsMapQuery({
  managerWallets,
  depositor,
}: {
  managerWallets: string[] | undefined;
  depositor: string;
}) {
  return useQuery<OnChainVaultDepositorsMap>({
    queryKey: [
      'vaultsOnChain',
      'depositorMulticall',
      managerWallets,
      depositor,
    ],
    queryFn: !managerWallets
      ? skipToken
      : async () => {
          // return {} as OnChainVaultDepositorsMap; // TEST_VAULTS
          const list =
            await getVaultAggregatorContract().loadVaultDepositorsByDepositorWallet(
              managerWallets,
              depositor
            );
          return managerWallets.reduce((map, managerWallet, index) => {
            const depositorObj = list[index];
            map[managerWallet] = depositorObj
              ? {
                  costBasis: depositorObj.costBasis,
                  initialDepositTimestampInS:
                    depositorObj.initialDepositTimestampInS,
                  owedQuantity: depositorObj.owedQuantity,
                }
              : undefined;
            return map;
          }, {} as OnChainVaultDepositorsMap);
        },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

// export function useVaultOnChainDepositsQuery({ managerWallet }: { managerWallet: string; }) {
//   return useQuery({
//     queryKey: ['vaultOnchain', 'deposits', managerWallet],
//     queryFn: () => getAggregatorContract().loadVaultDeposits([managerWallet]),
//     staleTime: seconds(15),
//     refetchInterval: minutes(5),
//   });
// }

export function useVaultOnChainSummaryQuery({
  managerWallet,
}: {
  managerWallet: string;
}) {
  return useQuery({
    queryKey: ['vaultsOnChain', 'summary', managerWallet],
    queryFn: () =>
      getVaultProviderContract()
        .loadVaultSummary(managerWallet)
        .then(response => ({
          // Note: we cannot use spread as it would not be serializable
          didManagerWalletWithdrawExit: response.didManagerWalletWithdrawExit,
          exitedAccountValue: response.exitedAccountValue,
          exitedAtTimestampInS: response.exitedAtTimestampInS,
          exitedTotalOwedQuantity: response.exitedTotalOwedQuantity,
          isDepositEnabled: response.isDepositEnabled,
          isExited: response.isExited,
          isLiquidated: response.isLiquidated,
          managerLockedQuantity: response.managerLockedQuantity,
          depositorNetDeposits: response.depositorNetDeposits,
          numDepositorWallets: response.numDepositorWallets,
          totalLockedQuantity: response.totalLockedQuantity,
          totalOwedQuantity: response.totalOwedQuantity,
          // totalPendingDepositQuantity: response.totalPendingDepositQuantity,
          /** Transformed chain data with already converted to Decimal from PIP */
          configurationFieldsTransformed: transformConfigurationFields(
            response.configurationFields
          ),
        })),
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

export function useVaultOnChainQuantityAvailableForExitWithdrawalQuery({
  managerWallet,
}: {
  managerWallet: string | undefined;
}) {
  return useQuery({
    queryKey: [
      'vaultsOnChain',
      'quantityAvailableForExitWithdrawalData',
      managerWallet,
    ],
    queryFn: !managerWallet
      ? skipToken
      : async () => {
          const quantityBN =
            await getExchangeContract().loadQuoteQuantityAvailableForExitWithdrawal(
              managerWallet
            );
          const quantityInDecimal = pipToDecimal(quantityBN);
          return { quantityInDecimal };
        },
    staleTime: seconds(15),
    refetchInterval: minutes(5),
  });
}

// export function useVaultOnChainVaultExitReadyStatusQuery({ managerWallet }: {  managerWallet: string | undefined; }) {
//   return useQuery({
//     queryKey: ['vaultsOnChain', 'onChainVaultExitReadyStatus', managerWallet],
//     queryFn: !managerWallet ? skipToken : async () => {
//       const readyToExit = await fetchOnChainVaultExitReadyStatus(managerWallet);
//       return readyToExit;
//     },
//     staleTime: seconds(15),
//     refetchInterval: minutes(5),
//   });
// }
